"use client";

import * as React from "react";
import { cn } from "@baluu/ui/lib/utils";
import { Input } from "@baluu/ui/components/input";
import { Textarea } from "@baluu/ui/components/textarea";
import { Checkbox } from "@baluu/ui/components/checkbox";
import { Label } from "@baluu/ui/components/label";
import { Button } from "@baluu/ui/components/button";
import { Calendar } from "@baluu/ui/components/calendar";
import { ResponsiveSelect } from "@baluu/ui/components/responsive-select";
import { CheckoutSteps } from "@baluu/ui/components/checkout/checkout-steps";
import {
  firstGuestIndexFor,
  hasRenderableQuestions,
  formHasRenderableQuestions,
  formHasRenderableRequiredQuestions,
  isQuestionRenderable,
} from "./utils/guest-questions";
import {
  buildQuestionPages,
  nextPageIndex,
  type QuestionSelection,
} from "./utils/question-pages";
import { answerLabelFor, applyAnswerUpdate } from "./utils/answer-entries";
import { validateEmail } from "./validation";
import { CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { format } from "date-fns";
import {
  StarRatingInput,
  CurrencyInput,
  DateTimeInput,
  UrlInput,
  MultiSelectCheckboxGroup,
} from "./field-inputs";
import { NoteMarkdown } from "./note-markdown";

type QuestionType =
  | "shortTextInput"
  | "numberInput"
  | "singleSelect"
  | "booleanInput"
  | "dateInput"
  | "noteInput"
  | "longTextInput"
  | "multiSelect"
  | "dateTimeInput"
  | "ratingInput"
  | "urlInput"
  | "currencyInput";

type SingleSelectOption = {
  _id: string;
  name: string;
  postOptionSelectedTarget?: string;
};

type Question = {
  _id: string;
  name?: string; // Used for targeting from single select options
  title: string;
  typeOfInput: QuestionType;
  isRequired?: boolean;
  isPerGuest?: boolean;
  order?: number;
  singleSelectOptions?: SingleSelectOption[];
  behaviorAfterQuestion?: string;
  specificNextClickQuestionAnchor?: string | number;
  /**
   * Discriminator for the three system identity questions used to collect
   * ADDITIONAL guests (Guest 2+). null / absent = a normal booking question.
   */
  systemRole?: "firstName" | "lastName" | "email" | null;
  /** Starts a new page at this question. See ./utils/question-pages. */
  pageBreakBefore?: boolean;
  /** Heading for the page this question starts. */
  pageTitle?: string;
};

type UserFormAnswer = {
  index: number;
  guestIndex: number | null;
  label: string;
  value: string | boolean | Date | null | number | string[];
  /**
   * Self-describing role tag for the system identity questions, stamped so the
   * server can map an answer to the guest field it fills (firstname/surname/email)
   * without relying on the question order. Absent for owner-authored questions.
   */
  systemRole?: "firstName" | "lastName" | "email" | null;
  /** Stable question _id. Absent on answers recorded before Aug 2026. */
  questionId?: string;
  /** Stable question identifier (`question.name`). Absent on historic answers. */
  name?: string;
};

type UserFormNavState = {
  isFirst: boolean;
  isLast: boolean;
  /** Whether the footer "Continue" may proceed (current question valid, or at the end view). */
  canAdvance: boolean;
};

/** Imperative handle for footer-driven navigation (oneAtTime mode). */
type UserFormStepHandle = {
  goNext: () => void;
  goPrevious: () => void;
};

type UserFormStepProps = {
  questions: Question[];
  values: UserFormAnswer[];
  totalGuests?: number;
  mode?: "all" | "oneAtTime";
  onChange: (values: UserFormAnswer[]) => void;
  onValidationChange?: (isValid: boolean) => void;
  /**
   * When true, the component hides its internal Previous/Next buttons and lets
   * the parent drive question navigation through the imperative handle, while
   * reporting nav state via onNavStateChange. Opt-in so non-migrated flows keep
   * their internal buttons.
   */
  footerDrivenNav?: boolean;
  onNavStateChange?: (state: UserFormNavState) => void;
  /**
   * Whether the system identity questions (firstName/lastName/email) should be
   * rendered as additional-guest collectors. True only when the flow relies on
   * the booking form for additional guests (the personal-details step is
   * skipped). When false those questions never render, so the personal step
   * remains the single collector and there's no double entry. Defaults to
   * false so flows only render additional-guest questions when they opt in.
   */
  collectSystemGuests?: boolean;
  /**
   * When true, the school collects per-guest identity via the participants
   * step, so the system identity questions (firstName/lastName/email) are
   * suppressed here. Owner-authored per-guest questions still render.
   */
  participantsEnabled?: boolean;
  className?: string;
};

/**
 * Unanswered for validation purposes: null/undefined/empty, or a string of
 * only whitespace — a required name answered with a space must not pass and
 * then be trimmed to "" server-side.
 */
function isBlankAnswerValue(
  value: string | boolean | Date | null | number | string[] | undefined,
): boolean {
  if (Array.isArray(value)) return value.length === 0;
  if (value === null || value === undefined || value === "") return true;
  return typeof value === "string" && value.trim() === "";
}

const UserFormStep = React.forwardRef<UserFormStepHandle, UserFormStepProps>(
  function UserFormStep(
    {
      questions,
      values: initialValues,
      totalGuests = 1,
      mode = "all",
      onChange,
      onValidationChange,
      footerDrivenNav = false,
      onNavStateChange,
      collectSystemGuests = false,
      participantsEnabled = false,
      className,
    }: UserFormStepProps,
    ref,
  ) {
  // Use internal state like the old UserFormQuestionary component
  // This ensures immediate response and avoids parent state corruption issues
  const [internalValues, setInternalValues] =
    React.useState<UserFormAnswer[]>(initialValues);

  // Track if we've made local changes - don't sync from parent if we have local edits
  const hasLocalChangesRef = React.useRef(false);

  // Only sync from parent on initial mount or explicit reset (when parent passes empty array after having values)
  const prevInitialValuesLengthRef = React.useRef(initialValues.length);
  React.useEffect(() => {
    // If we have local changes, don't sync from parent (parent state may be corrupted)
    if (hasLocalChangesRef.current) {
      return;
    }
    // Only sync if this looks like an intentional reset (parent went from having values to empty)
    // or if this is the initial mount
    if (initialValues.length === 0 && prevInitialValuesLengthRef.current > 0) {
      setInternalValues([]);
      hasLocalChangesRef.current = false;
    }
    prevInitialValuesLengthRef.current = initialValues.length;
  }, [initialValues]);

  const sortedQuestions = React.useMemo(
    () => [...questions].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [questions],
  );

  const pages = React.useMemo(
    () =>
      buildQuestionPages(sortedQuestions, {
        mode,
        totalGuests,
        collectSystemGuests,
        participantsEnabled,
      }),
    [sortedQuestions, mode, totalGuests, collectSystemGuests, participantsEnabled],
  );

  // A single page means there is nothing to navigate: render everything at once.
  // This is the "all" mode with no breaks — 96% of live listings.
  const isSinglePage = pages.length <= 1;

  const [currentPageIndex, setCurrentPageIndex] = React.useState(0);
  const [visitedPages, setVisitedPages] = React.useState<number[]>([]);
  // Scoped to the exact question the buyer answered — see `nextPageIndex` for
  // why a bare option index isn't enough once a page can hold two single-selects.
  const [singleSelectSelection, setSingleSelectSelection] =
    React.useState<QuestionSelection | null>(null);

  const isFirst = currentPageIndex <= 0;
  // `pages.length` is the terminal position: every page has been completed.
  const isLast = currentPageIndex >= pages.length;

  const findAnswer = (
    questionIndex: number,
    guestIndex: number | null,
  ): UserFormAnswer | undefined => {
    return internalValues.find(
      (item) => item.index === questionIndex && item.guestIndex === guestIndex,
    );
  };

  // Helper function to update internal state and notify parent
  const updateInternalValues = (newValues: UserFormAnswer[]) => {
    hasLocalChangesRef.current = true; // Mark that we have local changes
    setInternalValues(newValues);
    // Notify parent of changes
    onChange(newValues);
  };

  const updateAnswer = (
    questionIndex: number,
    guestIndex: number | null,
    label: string,
    value: string | boolean | Date | null | number | string[],
    question: Question,
  ) => {
    updateInternalValues(
      applyAnswerUpdate(internalValues, {
        questionIndex,
        guestIndex,
        label,
        value,
        question,
      }),
    );
  };

  const isQuestionValid = (questionIndex: number): boolean => {
    const question = sortedQuestions[questionIndex];
    if (!question?.isRequired) return true;
    // A question that renders no input can't be answered, so it must never block
    // the form (hidden system identity questions: no additional guests, or the
    // personal step is collecting them instead).
    if (!isQuestionRenderable(question, totalGuests, collectSystemGuests, participantsEnabled)) {
      return true;
    }

    if (question.isPerGuest) {
      for (
        let guestIndex = firstGuestIndexFor(question);
        guestIndex < totalGuests;
        guestIndex++
      ) {
        const answer = findAnswer(questionIndex, guestIndex);
        if (!answer || isBlankAnswerValue(answer.value)) {
          return false;
        }
        // System email questions collect a real address for an additional guest;
        // enforce a format check (plain text inputs are otherwise unvalidated).
        // Same validator as the buyer's email, so guests aren't held to a
        // weaker standard.
        if (
          question.systemRole === "email" &&
          typeof answer.value === "string" &&
          !validateEmail(answer.value).isValid
        ) {
          return false;
        }
      }
      return true;
    }

    const answer = findAnswer(questionIndex, null);
    return !!answer && !isBlankAnswerValue(answer.value);
  };

  /** Every required question on a page must be answered before advancing. */
  const isPageValid = (pageIndex: number): boolean => {
    const page = pages[pageIndex];
    if (!page) return true;
    return page.questionIndices.every((i) => isQuestionValid(i));
  };

  React.useEffect(() => {
    if (!onValidationChange) return;

    if (isSinglePage) {
      onValidationChange(pages.every((_, i) => isPageValid(i)));
    } else {
      // Paged: the step is complete only once the buyer has paged to the end.
      onValidationChange(isLast);
    }
  }, [internalValues, isLast, isSinglePage, pages, onValidationChange]);

  const handleNextQuestion = () => {
    setVisitedPages([...visitedPages, currentPageIndex]);
    setCurrentPageIndex(
      nextPageIndex(pages, sortedQuestions, currentPageIndex, singleSelectSelection),
    );
    setSingleSelectSelection(null);
  };

  const handlePreviousQuestion = () => {
    const newVisited = [...visitedPages];
    const prevIndex = newVisited.pop() ?? 0;
    setVisitedPages(newVisited);
    setCurrentPageIndex(prevIndex);
  };

  const handleSingleSelectChange = (questionIndex: number, optionIndex: number) => {
    setSingleSelectSelection({ questionIndex, optionIndex });
  };

  // Expose question navigation to the parent so the checkout footer can drive it.
  React.useImperativeHandle(ref, () => ({
    goNext: handleNextQuestion,
    goPrevious: handlePreviousQuestion,
  }));

  // Report nav state so the parent footer can set its Continue/Back disabled
  // state. In "all" mode there is no per-question nav, so the footer simply
  // leaves the step once every question is valid.
  const navState: UserFormNavState = isSinglePage
    ? {
        isFirst: true,
        isLast: true,
        canAdvance: pages.every((_, i) => isPageValid(i)),
      }
    : {
        isFirst,
        isLast,
        canAdvance: isLast ? true : isPageValid(currentPageIndex),
      };

  React.useEffect(() => {
    if (footerDrivenNav) onNavStateChange?.(navState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    footerDrivenNav,
    onNavStateChange,
    navState.isFirst,
    navState.isLast,
    navState.canAdvance,
  ]);

  const visiblePage = isSinglePage ? pages[0] : pages[currentPageIndex];

  return (
    <div className={cn("space-y-6", className)}>
      <CheckoutSteps.Header
        title="Customer Information"
        description="Please provide the necessary information so that we can continue with your booking."
      />

      {!isSinglePage && !isLast && (
        <p className="text-sm text-muted-foreground">
          Step {currentPageIndex + 1} of {pages.length}
        </p>
      )}

      {visiblePage?.title && (
        <h3 className="font-medium text-foreground">{visiblePage.title}</h3>
      )}

      {!isSinglePage && isLast ? (
        // Terminal page: no more questions to show. Point the buyer at the
        // footer's Continue button rather than leaving a blank page — plain
        // text, not the deleted celebratory card.
        <p className="text-sm text-muted-foreground">
          All done here — click Continue below to proceed with your booking.
        </p>
      ) : (
        <div className="space-y-6">
          {visiblePage?.questionIndices.map((questionIndex) => (
            <QuestionRenderer
              key={sortedQuestions[questionIndex]!._id}
              question={sortedQuestions[questionIndex]!}
              index={questionIndex}
              totalGuests={totalGuests}
              collectSystemGuests={collectSystemGuests}
              participantsEnabled={participantsEnabled}
              findAnswer={findAnswer}
              updateAnswer={updateAnswer}
              onSingleSelectChange={
                isSinglePage ? () => {} : handleSingleSelectChange
              }
            />
          ))}
        </div>
      )}

      {!isSinglePage && !footerDrivenNav && (
        <div className="flex justify-between gap-4">
          {!isFirst && (
            <Button variant="outline" onClick={handlePreviousQuestion}>
              <ChevronLeft className="w-4 h-4 mr-2" />
              Previous
            </Button>
          )}
          {!isLast && (
            <Button
              onClick={handleNextQuestion}
              disabled={!isPageValid(currentPageIndex)}
              className="ml-auto"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
});

type QuestionRendererProps = {
  question: Question;
  index: number;
  totalGuests: number;
  collectSystemGuests: boolean;
  participantsEnabled: boolean;
  findAnswer: (
    questionIndex: number,
    guestIndex: number | null,
  ) => UserFormAnswer | undefined;
  updateAnswer: (
    questionIndex: number,
    guestIndex: number | null,
    label: string,
    value: string | boolean | Date | null | number | string[],
    question: Question,
  ) => void;
  /** `questionIndex` is always this renderer's own `index` — see call sites below. */
  onSingleSelectChange: (questionIndex: number, optionIndex: number) => void;
};

function QuestionRenderer({
  question,
  index,
  totalGuests,
  collectSystemGuests,
  participantsEnabled,
  findAnswer,
  updateAnswer,
  onSingleSelectChange,
}: QuestionRendererProps) {
  if (question.isPerGuest) {
    // System identity questions collect additional guests only, so they start at
    // guest index 1 (the buyer is Guest 1, collected earlier) and render nothing
    // when there are no additional guests or the personal step collects them.
    if (!isQuestionRenderable(question, totalGuests, collectSystemGuests, participantsEnabled)) {
      return null;
    }
    const firstGuestIndex = firstGuestIndexFor(question);

    return (
      <div className="space-y-4">
        {Array.from(
          { length: totalGuests - firstGuestIndex },
          (_, offset) => {
            const guestIndex = firstGuestIndex + offset;
            const answer = findAnswer(index, guestIndex);
            const value = answer?.value ?? "";

            return (
              <div
                key={`${index}-guest-${guestIndex}`}
                className="p-2 bg-card rounded-lg border border-border"
              >
                <h4 className="font-medium text-foreground mb-3">
                  Guest {guestIndex + 1}
                </h4>
                <QuestionInput
                  question={question}
                  value={value}
                  onChange={(newValue) => {
                    updateAnswer(
                      index,
                      guestIndex,
                      answerLabelFor(question, guestIndex),
                      newValue,
                      question,
                    );
                  }}
                  onSingleSelectChange={(optionIndex) =>
                    onSingleSelectChange(index, optionIndex)
                  }
                />
              </div>
            );
          },
        )}
      </div>
    );
  }

  const answer = findAnswer(index, null);
  const value = answer?.value ?? "";

  return (
    <div className="p-2 bg-card rounded-lg border border-border">
      <QuestionInput
        question={question}
        value={value}
        onChange={(newValue) => {
          updateAnswer(index, null, answerLabelFor(question, null), newValue, question);
        }}
        onSingleSelectChange={(optionIndex) =>
          onSingleSelectChange(index, optionIndex)
        }
      />
    </div>
  );
}

type QuestionInputProps = {
  question: Question;
  value: string | boolean | Date | null | number | string[] | undefined;
  onChange: (value: string | boolean | Date | null | number | string[]) => void;
  onSingleSelectChange?: (optionIndex: number) => void;
};

function QuestionInput({
  question,
  value,
  onChange,
  onSingleSelectChange,
}: QuestionInputProps) {
  // QuestionRenderer renders this component once per guest for an isPerGuest
  // question, passing the same `question` each time, so an id built only from
  // question._id collides across guests. `<label for>` resolves through
  // getElementById (first match wins), which made every guest's label toggle
  // the first guest's checkbox. Namespacing per instance fixes that, and also
  // covers the same question being rendered by more than one form on a page.
  // Same fix as MultiSelectCheckboxGroup in field-inputs.tsx.
  const instanceId = React.useId();
  const labelText = question.isRequired
    ? `${question.title} *`
    : question.title;

  switch (question.typeOfInput) {
    case "shortTextInput":
      return (
        <div className="space-y-2">
          <Label>{labelText}</Label>
          <Textarea
            value={(value as string) || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Please enter your information here"
            className="min-h-[80px]"
          />
        </div>
      );

    case "numberInput":
      return (
        <div className="space-y-2">
          <Label>{labelText}</Label>
          <Input
            type="number"
            min={0}
            value={(value as string) || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Enter a number"
          />
        </div>
      );

    case "singleSelect": {
      // Filter out options with empty or whitespace-only names (invalid for Radix Select)
      const validOptions = question.singleSelectOptions?.filter(
        (option) => option.name && option.name.trim() !== ""
      ) ?? [];

      // Transform to ResponsiveSelect format
      const selectOptions = validOptions.map((option) => ({
        value: option.name,
        label: option.name,
      }));

      return (
        <div className="space-y-2">
          <Label>{labelText}</Label>
          <ResponsiveSelect
            value={(value as string) || ""}
            onValueChange={(v) => {
              // Track which option was selected for smart navigation
              const optionIndex = question.singleSelectOptions?.findIndex(
                (option) => option.name === v,
              );
              if (optionIndex !== undefined && optionIndex !== -1) {
                onSingleSelectChange?.(optionIndex);
              }
              onChange(v);
            }}
            options={selectOptions}
            placeholder="Select an option"
            title={question.title}
            container={null}
            zIndex="z-[10002]"
          />
        </div>
      );
    }

    case "booleanInput": {
      // One id for both the control and its label so the two can't drift.
      const checkboxId = `${instanceId}-question-${question._id}`;

      return (
        <div className="flex items-center space-x-2">
          <Checkbox
            id={checkboxId}
            checked={(value as boolean) || false}
            onCheckedChange={(checked) => onChange(!!checked)}
          />
          <Label htmlFor={checkboxId} className="cursor-pointer">
            {question.title}
          </Label>
        </div>
      );
    }

    case "dateInput": {
      const selectedDate =
        value instanceof Date
          ? value
          : typeof value === "string" && value
            ? new Date(value)
            : undefined;

      // Calculate reasonable year range (100 years back, 10 years forward)
      const currentYear = new Date().getFullYear();
      const fromYear = currentYear - 100;
      const toYear = currentYear + 10;

      return (
        <div className="space-y-3">
          <Label>{labelText}</Label>

          {/* Selected date display */}
          {selectedDate && (
            <div className="flex items-center gap-2 p-3 bg-green-100 dark:bg-green-900 rounded-lg border border-green-500-border">
              <CalendarIcon className="h-4 w-4 text-green-500-foreground" />
              <span className="text-sm font-medium text-green-500-foreground">
                {format(selectedDate, "EEEE, MMMM do, yyyy")}
              </span>
            </div>
          )}

          {/* Always visible calendar with month/year dropdowns */}
          <div className="border border-border rounded-lg p-2 bg-card">
            <Calendar
              mode="single"
              captionLayout="dropdown"
              fromYear={fromYear}
              toYear={toYear}
              defaultMonth={selectedDate || new Date()}
              selected={selectedDate}
              onSelect={(date) => onChange(date ?? null)}
              className="mx-auto"
            />
          </div>
        </div>
      );
    }

    case "noteInput":
      return (
        <div className="p-2 bg-muted rounded-lg">
          <NoteMarkdown className="text-muted-foreground">
            {question.title}
          </NoteMarkdown>
        </div>
      );

    case "longTextInput":
      return (
        <div className="space-y-2">
          <Label>{labelText}</Label>
          <Textarea value={(value as string) || ""} onChange={(e) => onChange(e.target.value)}
            placeholder="Please enter your information here" className="min-h-[140px]" />
        </div>
      );

    case "multiSelect": {
      const options = (question.singleSelectOptions ?? [])
        .map((o) => o.name).filter((n) => n && n.trim() !== "");
      // Persist the selection as a ", "-joined string, not a JS array, so
      // booking.userInputs[].value stays a string for every downstream reader
      // (confirmation emails, dashboard booking/session views, guest-list CSV
      // all assume a string — an array there drops the answer or crashes on
      // .trim()). The array shape lives only inside the checkbox group.
      const selected =
        typeof value === "string" && value !== "" ? value.split(", ") : [];
      return (
        <div className="space-y-2">
          <Label>{labelText}</Label>
          <MultiSelectCheckboxGroup options={options}
            value={selected}
            onChange={(v) => onChange(v.join(", "))} />
        </div>
      );
    }

    case "dateTimeInput":
      return (
        <div className="space-y-2">
          <Label>{labelText}</Label>
          <DateTimeInput value={(value as string) || ""} onChange={onChange} />
        </div>
      );

    case "ratingInput":
      return (
        <div className="space-y-2">
          <Label>{labelText}</Label>
          <StarRatingInput value={(value as string) || ""} onChange={onChange} />
        </div>
      );

    case "urlInput":
      return (
        <div className="space-y-2">
          <Label>{labelText}</Label>
          <UrlInput value={(value as string) || ""} onChange={onChange} />
        </div>
      );

    case "currencyInput":
      return (
        <div className="space-y-2">
          <Label>{labelText}</Label>
          <CurrencyInput value={(value as string) || ""} onChange={onChange} />
        </div>
      );

    default:
      return null;
  }
}

// Re-exported for callers that import this component through the
// `@baluu/ui/components/checkout/*` subpath (which only resolves .tsx files)
// and need to gate the step on whether any question actually renders.
export {
  UserFormStep,
  hasRenderableQuestions,
  formHasRenderableQuestions,
  formHasRenderableRequiredQuestions,
};
export type {
  Question,
  UserFormAnswer,
  UserFormStepProps,
  UserFormStepHandle,
  UserFormNavState,
};
